/* Parses a loss history CSV into rows the grid can stage. Pure and synchronous — no React, no
   network — matching lossStaging / lossValidation / saveLossChanges in this folder.

   Every coercion fails soft to a value the grid can flag, so a bad cell is fixed where the user can
   see it rather than back in the spreadsheet. Whatever is dropped or substituted is counted and
   reported: the one thing this must never do is quietly change a number. */

import { readString } from 'react-papaparse';

import { isIsoDate, toIsoDate } from './lossDates';

// The headers GET /applications/{id}/losses/download emits (Applications.LOSS_CSV_HEADERS), so a
// downloaded file is a valid input. total_incurred is recognized only so it is not reported as
// foreign; the database generates it, so its value is never read.
const HEADERS = [
  'location_id',
  'carrier',
  'claim_number',
  'status',
  'loss_cause',
  'occurred_at',
  'indemnity',
  'expense',
  'recovery',
  'total_incurred',
  'description',
  'source',
];

/* The columns a broker fills in: every header above except total_incurred, which is never read — a
   loss entered there instead of in indemnity would import with no amount — and source, which only
   marks the Skyward rows a hand-built file never holds. CRLF, as CSVFormat.EXCEL ends the download's
   header row. */
const TEMPLATE_HEADERS = HEADERS.filter((header) => header !== 'total_incurred' && header !== 'source');

export const lossCsvTemplate = () => `${TEMPLATE_HEADERS.join(',')}\r\n`;

// loss_history_status is an enum of exactly these, and the DAO casts to it.
const STATUSES = ['open', 'closed'];

const NO_COLUMNS = 'No recognized columns. Expected a loss history CSV with headers like claim_number, occurred_at, loss_cause and indemnity.';
const NO_ROWS = 'That file has no data rows.';

const norm = (value) => (value == null ? '' : String(value).trim());

const blankToNull = (value) => (value === '' ? null : value);

/* Returns a number, null for a blank cell, or NaN for something unreadable — the caller has to tell
   those last two apart, because a blank expense legitimately means zero while unreadable text does
   not mean anything and has to be reported. */
const toAmount = (raw) => {
  const cleaned = norm(raw).replace(/[$,]/g, '');
  if (cleaned === '') {
    return null;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

export const parseLossCsv = (text, { lossCauses = [], locations = [], existingClaimNumbers = [], skywardClaimNumbers = [] } = {}) => {
  const result = readString(text || '', { header: true, skipEmptyLines: 'greedy' });
  const fields = (result.meta && result.meta.fields) || [];
  const normalized = fields.map((field) => norm(field).toLowerCase());

  /* Papaparse keys each record by the header's raw text, so reaching a cell means going back through
     the normalized name — that is what lets " Claim_Number " resolve. */
  const keyFor = HEADERS.reduce((out, header) => {
    const index = normalized.indexOf(header);
    return index === -1 ? out : { ...out, [header]: fields[index] };
  }, {});

  if (!Object.keys(keyFor).length) {
    return { rows: [], warnings: [], error: NO_COLUMNS };
  }
  if (!result.data.length) {
    return { rows: [], warnings: [], error: NO_ROWS };
  }

  const cell = (record, header) => (keyFor[header] === undefined ? '' : norm(record[keyFor[header]]));

  /* A single incidental header match is enough to get past NO_COLUMNS, so a broker's own loss run
     would otherwise import every row blank and report success. Naming what had no home also covers a
     real column the grid does not carry, like `deductible` or `paid`. Nameless columns — a trailing
     separator on the header row — are not the user's to fix. */
  const ignoredHeaders = fields.map((field) => norm(field)).filter((field) => field !== '' && !HEADERS.includes(field.toLowerCase()));

  const causeBySpelling = lossCauses.reduce((out, lc) => ({ ...out, [norm(lc.lossCause).toLowerCase()]: lc.lossCause }), {});
  const locationIds = locations.map((location) => location.id);
  const lower = (claims) => claims.map((claim) => norm(claim).toLowerCase()).filter(Boolean);
  const knownClaims = lower(existingClaimNumbers);
  const knownSkywardClaims = lower(skywardClaimNumbers);

  const counts = { dates: 0, amounts: 0, causes: 0, locations: 0, statuses: 0, duplicates: 0, skywardDuplicates: 0, skipped: 0 };

  /* A Skyward row must never become a candidate for import, so it is filtered out here rather than
     inside the map below — coercing and counting it first, then discarding it, would still surface
     warnings (an unreadable date, an unknown cause) about a row nobody is importing. */
  const importable = result.data.filter((record) => {
    if (cell(record, 'source').toUpperCase() === 'SKYWARD') {
      counts.skipped += 1;
      return false;
    }
    return true;
  });

  const rows = importable.map((record) => {
    const occurredAt = toIsoDate(cell(record, 'occurred_at'));
    const unreadableDate = occurredAt != null && !isIsoDate(occurredAt);
    if (unreadableDate) {
      counts.dates += 1;
    }

    const amounts = {
      expense: toAmount(cell(record, 'expense')),
      incurred: toAmount(cell(record, 'indemnity')),
      recovery: toAmount(cell(record, 'recovery')),
    };
    if (Object.keys(amounts).some((key) => Number.isNaN(amounts[key]))) {
      counts.amounts += 1;
    }

    const rawCause = cell(record, 'loss_cause');
    const lossCause = causeBySpelling[rawCause.toLowerCase()] || null;
    if (rawCause !== '' && lossCause === null) {
      counts.causes += 1;
    }

    const rawLocation = cell(record, 'location_id');
    const locationId = locationIds.includes(rawLocation) ? rawLocation : null;
    if (rawLocation !== '' && locationId === null) {
      counts.locations += 1;
    }

    const rawStatus = cell(record, 'status').toLowerCase();
    const status = STATUSES.includes(rawStatus) ? rawStatus : 'open';
    if (rawStatus !== '' && !STATUSES.includes(rawStatus)) {
      counts.statuses += 1;
    }

    /* A Skyward twin is the worse case and gets its own count rather than being folded in: import only
       ever produces self-reported rows, and LossHistoryMerger does not suppress a Hail twin, so the
       loss double-counts in the grid and in the underwriting workbook. */
    const claimNumber = cell(record, 'claim_number');
    if (knownSkywardClaims.includes(claimNumber.toLowerCase())) {
      counts.skywardDuplicates += 1;
    } else if (knownClaims.includes(claimNumber.toLowerCase())) {
      counts.duplicates += 1;
    }

    return {
      locationId,
      carrier: blankToNull(cell(record, 'carrier')),
      claimNumber: blankToNull(claimNumber),
      status,
      lossCause,
      occurredAt: unreadableDate ? null : occurredAt,
      /* A blank expense or recovery is zero, the same resolution NEW_ROW_DEFAULTS makes. Unreadable
         text lands on zero too — staging blank would change nothing, since NEW_ROW_DEFAULTS resolves
         it back to zero at save time — so the warning below has to name the substitution outright. */
      expense: Number.isFinite(amounts.expense) ? amounts.expense : 0,
      recovery: Number.isFinite(amounts.recovery) ? amounts.recovery : 0,
      // indemnity stays blank so validation makes the user type it, rather than defaulting money.
      incurred: Number.isFinite(amounts.incurred) ? amounts.incurred : null,
      description: blankToNull(cell(record, 'description')),
    };
  });

  const warnings = [
    // First, because it describes the file rather than any of its rows.
    ignoredHeaders.length && `⚠ Ignored unrecognized columns: ${ignoredHeaders.join(', ')}`,
    counts.skipped &&
      `⚠ Skipped ${pluralize(counts.skipped, 'Skyward feed row', 'Skyward feed rows')} — importing ${
        counts.skipped === 1 ? 'it' : 'them'
      } would duplicate the feed`,
    counts.dates && `⚠ ${pluralize(counts.dates, 'row', 'rows')} had an occurrence date that could not be read — pick one before saving`,
    /* The count does not carry which of the three amounts failed, so both resolutions are stated as
       rules rather than as outcomes: asserting both would tell a row with a perfectly good indemnity
       that its indemnity imported blank. */
    counts.amounts &&
      `⚠ ${pluralize(
        counts.amounts,
        'row',
        'rows'
      )} had an amount that could not be read — an unreadable indemnity imports blank, an unreadable expense or recovery imports as 0; check ${
        counts.amounts === 1 ? 'it' : 'them'
      } before saving`,
    counts.causes && `⚠ ${pluralize(counts.causes, 'row', 'rows')} had a loss cause that is not on this product — pick one before saving`,
    counts.locations && `⚠ ${pluralize(counts.locations, 'row', 'rows')} referenced a location that is not on this application`,
    counts.statuses &&
      `⚠ ${pluralize(counts.statuses, 'row', 'rows')} had an unrecognized status — ${counts.statuses === 1 ? 'it' : 'they'} imported as Open`,
    counts.duplicates &&
      `⚠ ${counts.duplicates} of these claim numbers already ${
        counts.duplicates === 1 ? 'appears' : 'appear'
      } in the grid — importing adds a second copy`,
    counts.skywardDuplicates &&
      `⚠ ${counts.skywardDuplicates} of these claim numbers ${
        counts.skywardDuplicates === 1 ? 'matches' : 'match'
      } a Skyward feed loss — importing creates a duplicate self-reported copy`,
  ].filter(Boolean);

  return { rows, warnings, error: null };
};
